'use client';

import { createClient } from '@/utils/supabase/client';
import { useRouter } from 'next/navigation';
import Script from 'next/script';
import { useEffect } from 'react';

declare global {
  interface Window {
    google: any;
  }
}

type CredentialResponse = {
  credential: string;
  select_by?: string;
  clientId?: string;
  id?: string;
  response?: string;
};

const OneTapComponent = () => {
  const supabase = createClient();
  const router = useRouter();

  // generate nonce to use for google id token sign-in
  const generateNonce = async (): Promise<string[]> => {
    // Convert Uint8Array to array before spreading to avoid iteration error
    const randomValues = Array.from(crypto.getRandomValues(new Uint8Array(32)));
    const nonce = btoa(String.fromCharCode(...randomValues));
    const encoder = new TextEncoder();
    const encodedNonce = encoder.encode(nonce);
    const hashBuffer = await crypto.subtle.digest('SHA-256', encodedNonce);
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // ES2015+ required
    const hashedNonce = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    return [nonce, hashedNonce];
  };

  useEffect(() => {
    const initializeGoogleOneTap = () => {
      console.log('Initializing Google One Tap');
      window.addEventListener('load', async () => {
        const [nonce, hashedNonce] = await generateNonce();
        console.log('Nonce: ', nonce, hashedNonce);

        // check if there's already an existing session before initializing the one-tap UI
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session', error);
        }
        if (data.session) {
          router.push('/');
          return;
        }

        /* global google */
        // @ts-expect-error: google is injected by the Google One Tap script
        google.accounts.id.initialize({
          client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
          callback: async (response: CredentialResponse) => {
            try {
              // send id token returned in response.credential to supabase
              const { data, error } = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token: response.credential,
                nonce,
              });

              if (error) throw error;
              console.log('Session data: ', data);
              console.log('Successfully logged in with Google One Tap');

              // redirect to protected page
              router.push('/');
            } catch (error) {
              console.error('Error logging in with Google One Tap', error);
            }
          },
          nonce: hashedNonce,
          // with chrome's removal of third-party cookies, we need to use FedCM instead (https://developers.google.com/identity/gsi/web/guides/fedcm-migration)
          use_fedcm_for_prompt: true,
        });
        // @ts-expect-error: google is injected by the Google One Tap script
        google.accounts.id.prompt(); // Display the One Tap UI
      });
    };
    initializeGoogleOneTap();
    return () => window.removeEventListener('load', initializeGoogleOneTap);
  }, []);

  return (
    <>
      <Script src="https://accounts.google.com/gsi/client" />
      <div id="oneTap" className="fixed top-0 right-0 z-[100]" />
    </>
  );
};

export default OneTapComponent;
